import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Url } from "../url/url";

const toSlug = (str) =>
  str.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }, { size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header","font","size","bold","italic","underline","strike",
  "color","background","list","bullet","indent","align",
  "blockquote","code-block","link","image","video",
];

const S = `
  .sp-wrap{background:#f1f5f9;min-height:100vh;padding:24px 20px}
  .sp-card{background:#fff;border-radius:12px;box-shadow:0 2px 10px rgba(0,0,0,.07);overflow:hidden;max-width:100%}
  .sp-input{width:100%;padding:9px 13px;border:1px solid #d1d5db;border-radius:7px;font-size:14px;
    outline:none;box-sizing:border-box;font-family:inherit;background:#fff;color:#1f2937;
    transition:border-color .2s,box-shadow .2s}
  .sp-input:focus{border-color:#4f46e5;box-shadow:0 0 0 3px rgba(79,70,229,.1)}
  .sp-field{display:flex;flex-direction:column;gap:6px}
  .sp-btn-save{background:#4f46e5;color:#fff;border:none;border-radius:7px;padding:10px 28px;
    font-weight:600;cursor:pointer;font-size:14px;display:inline-flex;align-items:center;gap:8px;transition:background .2s}
  .sp-btn-save:hover{background:#4338ca}
  .sp-btn-save:disabled{opacity:.6;cursor:not-allowed}
  .sp-btn-reset{padding:10px 20px;border-radius:7px;border:1px solid #d1d5db;background:#fff;
    cursor:pointer;font-weight:600;font-size:14px;transition:background .15s}
  .sp-btn-reset:hover{background:#f9fafb}
  .sp-seo-box{background:#f8faff;border:1px solid #e0e7ff;border-radius:10px;padding:16px 18px}
  .sp-seo-ttl{font-size:11px;font-weight:700;color:#6366f1;text-transform:uppercase;letter-spacing:1px;margin:0 0 14px}
  .sp-quill .ql-container{min-height:280px;font-size:14px}
  .sp-quill .ql-editor{min-height:280px}
  .sp-quill .ql-toolbar{border-radius:7px 7px 0 0;background:#f9fafb}
  .sp-quill .ql-container{border-radius:0 0 7px 7px}
  @keyframes spIn{from{transform:translateX(50px);opacity:0}to{transform:translateX(0);opacity:1}}
  .sp-toast{position:fixed;top:20px;right:20px;z-index:9999;padding:12px 18px;border-radius:9px;
    color:#fff;font-weight:600;font-size:14px;display:flex;align-items:center;gap:10px;
    min-width:260px;box-shadow:0 8px 24px rgba(0,0,0,.18);animation:spIn .2s ease}
`;

export default function StaticPageCreate() {
  const navigate = useNavigate();

  const EMPTY = {
    title:"", slug:"", content:"", metaTitle:"", metaKeywords:"",
    metaDescription:"", headScript:"", bodyScript:"", isActive:true
  };

  const [form,    setForm]    = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState(null);

  const showToast = useCallback((msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const set = (name, val) => setForm(p => ({ ...p, [name]: val }));

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    set(name, type === "checkbox" ? checked : value);
  };

  const handleTitleBlur = () => {
    if (!form.slug && form.title) set("slug", toSlug(form.title));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim()) { showToast("Title required hai", false); return; }
    if (!form.content?.replace(/<[^>]+>/g,"").trim()) { showToast("Content required hai", false); return; }
    setLoading(true);
    try {
      await axios.post(`${Url}/api/staticpage`, form);
      showToast("Page create ho gayi 🎉");
      setForm(EMPTY);
    } catch(err) {
      showToast(err?.response?.data?.message || "Error aaya", false);
    } finally { setLoading(false); }
  };

  /* eslint-disable jsx-a11y/label-has-associated-control */
  return (
    <div className="sp-wrap">
      <style>{S}</style>

      {toast && (
        <div className="sp-toast" style={{ background: toast.ok ? "#22c55e" : "#ef4444" }}>
          {toast.ok ? "✅" : "❌"} {toast.msg}
          <button type="button" onClick={() => setToast(null)}
            style={{ marginLeft:"auto",background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:18,lineHeight:1 }}>×</button>
        </div>
      )}

      {/* Header */}
      <div className="sp-card" style={{ padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16 }}>
        <h5 style={{ margin:0,fontWeight:700,fontSize:16 }}>➕ Add Static Page</h5>
        <button type="button" className="sp-btn-reset"
          onClick={() => navigate("/dashboard/StaticPagesList")}>
          📄 View All Pages
        </button>
      </div>

      {/* Form */}
      <div className="sp-card" style={{ padding:"22px" }}>
        <form onSubmit={handleSubmit} noValidate>

          {/* Title + Slug */}
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:18 }}>
            <label className="sp-field">
              <span>Title <span style={{ color:"#ef4444" }}>*</span></span>
              <input type="text" className="sp-input" name="title"
                placeholder="Enter Title" value={form.title}
                onChange={handleChange} onBlur={handleTitleBlur} />
            </label>
            <label className="sp-field">
              <span>Slug <span style={{ color:"#ef4444" }}>*</span></span>
              <input type="text" className="sp-input" name="slug"
                placeholder="auto-generated-from-title"
                value={form.slug} onChange={handleChange} />
            </label>
          </div>

          {/* Content */}
          <div style={{ marginBottom:18 }}>
            <p style={{ fontSize:13,fontWeight:600,color:"#374151",margin:"0 0 6px" }}>
              Content <span style={{ color:"#ef4444" }}>*</span>
            </p>
            <div className="sp-quill">
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={val => set("content", val)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Page ka content yahan likhein..."
              />
            </div>
          </div>

          {/* SEO */}
          <div className="sp-seo-box" style={{ marginBottom:18 }}>
            <p className="sp-seo-ttl">🔍 SEO Information</p>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14 }}>
              <label className="sp-field">
                <span style={{ fontSize:12 }}>Meta Title</span>
                <input type="text" className="sp-input" name="metaTitle"
                  placeholder="Enter SEO Title" value={form.metaTitle}
                  onChange={handleChange} style={{ fontSize:13 }} />
              </label>
              <label className="sp-field">
                <span style={{ fontSize:12 }}>Meta Keywords</span>
                <input type="text" className="sp-input" name="metaKeywords"
                  placeholder="Enter SEO Keywords" value={form.metaKeywords}
                  onChange={handleChange} style={{ fontSize:13 }} />
              </label>
            </div>
            <label className="sp-field" style={{ marginBottom:14 }}>
              <span style={{ fontSize:12 }}>Meta Description</span>
              <textarea className="sp-input" name="metaDescription" rows={3}
                placeholder="Enter SEO Description"
                value={form.metaDescription} onChange={handleChange}
                style={{ resize:"vertical",fontSize:13 }} />
            </label>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
              <label className="sp-field">
                <span style={{ fontSize:12 }}>Script for HEAD</span>
                <textarea className="sp-input" name="headScript" rows={2}
                  placeholder="Script for HEAD Section"
                  value={form.headScript} onChange={handleChange}
                  style={{ resize:"vertical",fontSize:13,fontFamily:"monospace" }} />
              </label>
              <label className="sp-field">
                <span style={{ fontSize:12 }}>Script for BODY</span>
                <textarea className="sp-input" name="bodyScript" rows={2}
                  placeholder="Script for BODY Section"
                  value={form.bodyScript} onChange={handleChange}
                  style={{ resize:"vertical",fontSize:13,fontFamily:"monospace" }} />
              </label>
            </div>
          </div>

          {/* Active Toggle */}
          <div style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 16px",
            borderRadius:9,marginBottom:18,border:"1px solid",
            borderColor: form.isActive ? "#bbf7d0" : "#e5e7eb",
            background:  form.isActive ? "#f0fdf4"  : "#f9fafb" }}>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch"
                id="sp-active" name="isActive" checked={form.isActive}
                onChange={handleChange}
                style={{ width:"3rem",height:"1.5rem",cursor:"pointer" }} />
              <label className="form-check-label" htmlFor="sp-active"
                style={{ fontWeight:600,color: form.isActive ? "#16a34a" : "#6b7280",cursor:"pointer" }}>
                {form.isActive ? "Active" : "Inactive"}
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display:"flex",gap:12 }}>
            <button type="submit" className="sp-btn-save" disabled={loading}>
              {loading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
              Save Page
            </button>
            <button type="button" className="sp-btn-reset" onClick={() => setForm(EMPTY)}>
              Reset
            </button>
          </div>

        </form>
      </div>
    </div>
  );
  /* eslint-enable jsx-a11y/label-has-associated-control */
}